import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * The data-quality facts behind the persistent strip (design 12.2, NFR-4 / FR-20).
 *
 * "A chart is never read without knowing what might be missing": the strip always shows whether
 * sources are silent, whether items were dropped in the current range, and whether sampling is in
 * effect. The shell renders the strip from this context; the facts are PUBLISHED by the screens and
 * the live stream as they load (a later task — the overview's ingest-health strip, the stream's
 * data-quality frames — calls `report`). Until something reports, the strip shows an honest
 * "unknown" state rather than a reassuring empty one ("unverifiable is never green", design 12.3).
 *
 * NFR-4 AC1: drops are counted and reported. AC2: a gap, sampling, a stale source or clock skew is
 * shown in the indicator. AC3: exact vs estimated is distinguished — carried by `exact` on the
 * sampling fact so the strip can say "est." where a count is sampled.
 */

/** A source that has gone quiet within the current range. */
export interface SilentSource {
  readonly sourceId: string;
  /** Human label (service/name) if known, else the id. */
  readonly label: string;
  /** Milliseconds since the source was last heard from, if known. */
  readonly silentForMs: number | null;
}

/** Drops observed in the current range, by stage (NFR-4 AC1). */
export interface DropFacts {
  /** Dropped at the source before send. */
  readonly atSource: number;
  /** Rejected or shed at the edge. */
  readonly atEdge: number;
  /** Dropped in the pipeline (vocabulary, cardinality, validation). */
  readonly inPipeline: number;
}

/** Sampling in effect (NFR-4 AC2/AC3). */
export interface SamplingFacts {
  /** True when allow-class events are being sampled (so counts are estimates). */
  readonly active: boolean;
  /** The effective sample rate (0..1) when known, else null. */
  readonly rate: number | null;
  /** False when any affected count is an estimate ("est."); true when all shown counts are exact. */
  readonly exact: boolean;
}

/**
 * The whole data-quality snapshot. Every field is nullable/empty-able and starts "unknown" so the
 * strip can distinguish "nothing is wrong" from "we don't know yet".
 */
export interface DataQualitySnapshot {
  /** Null until any screen/stream has reported; then the (possibly empty) list of silent sources. */
  readonly silentSources: readonly SilentSource[] | null;
  /** Null until reported; then the per-stage drop counts in range. */
  readonly drops: DropFacts | null;
  /** Null until reported; then the sampling state. */
  readonly sampling: SamplingFacts | null;
  /** Clock skew seen on any source in range, in milliseconds, if reported (NFR-4 AC2). */
  readonly maxClockSkewMs: number | null;
  /** True when a batch-sequence gap affects the current range (NFR-4 AC2). */
  readonly hasSequenceGap: boolean | null;
}

const UNKNOWN: DataQualitySnapshot = {
  silentSources: null,
  drops: null,
  sampling: null,
  maxClockSkewMs: null,
  hasSequenceGap: null,
};

export interface DataQualityApi extends DataQualitySnapshot {
  /** Merge reported facts into the snapshot. Only the provided fields change; others keep state. */
  report(facts: Partial<DataQualitySnapshot>): void;
  /** Reset to the unknown state (e.g. on a range or tenant change, before new facts arrive). */
  reset(): void;
}

const DataQualityContext = createContext<DataQualityApi | null>(null);

export function DataQualityProvider(props: { children: ReactNode }): ReactNode {
  const [snapshot, setSnapshot] = useState<DataQualitySnapshot>(UNKNOWN);

  const report = useCallback<DataQualityApi["report"]>((facts) => {
    setSnapshot((current) => ({ ...current, ...facts }));
  }, []);

  const reset = useCallback<DataQualityApi["reset"]>(() => setSnapshot(UNKNOWN), []);

  const value = useMemo<DataQualityApi>(
    () => ({ ...snapshot, report, reset }),
    [snapshot, report, reset],
  );

  return <DataQualityContext.Provider value={value}>{props.children}</DataQualityContext.Provider>;
}

export function useDataQuality(): DataQualityApi {
  const ctx = useContext(DataQualityContext);
  if (ctx === null) throw new Error("useDataQuality must be used within <DataQualityProvider>");
  return ctx;
}
